import { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { apiBase, authToken } from '../api/apiBase';
import { getRexySocket } from '../hooks/useRexySocket';
import type { MackScheduledEvent, MackUnscheduledEvent, MackSheetPulse, MackPulseDetails, MackDownstreamDestination } from '../components/mack/CalendarTab';

export interface MackCalendarView {
  scheduled: MackScheduledEvent[];
  unscheduled: MackUnscheduledEvent[];
  pulse: MackSheetPulse[];
  updated_at?: string | null;
  status: string;
  window_start_date?: string;
  window_end_date?: string;
  total_rows: number;
  filtered_rows: number;
  source: string;
  view?: 'historical';
  history_count?: number;
  history_series_count?: number;
  history_start_date?: string | null;
  history_end_date?: string | null;
  official_observation_count?: number;
  official_observation_exclusions: string[];
  coverage_gap_count?: number;
  history_retention?: 'PERMANENT';
  history_workbook?: { name: string; event_count: number | null; vintage_count: number | null; updated_at?: string; state: 'AVAILABLE' | 'SYNC_REQUIRED' };
  calendar_completion?: { state: string; reason: string; attempted_at?: string; source_url?: string; next_reconciliation_at?: string };
  alerts: { event_id: string }[];
  set_aside: unknown[];
  activation_forecast: (MackPulseDetails & { event_id: string; event_name: string; affected_sheet: string; affected_block: string;
    activation_at?: string; release_at?: string; quiet_at?: string; projected_quiet_at?: string | null; quiet_state?: string; impact: string; currency: string; pulse_at?: string; pulse_registered?: boolean; downstream_destinations?: MackDownstreamDestination[] })[];
}

export interface CalendarWindow { window_start_date: string; window_end_date: string; view?: 'historical' }
export function calendarWeek(offset = 0): CalendarWindow {
  const today = new Intl.DateTimeFormat('en-CA', { timeZone: 'Australia/Hobart',
    year: 'numeric', month: '2-digit', day: '2-digit' }).formatToParts(new Date());
  const part = (type: string) => today.find(p => p.type === type)!.value;
  const date = new Date(`${part('year')}-${part('month')}-${part('day')}T12:00:00Z`);
  date.setUTCDate(date.getUTCDate() - (date.getUTCDay() + 6) % 7 + offset * 7);
  const start = date.toISOString().slice(0, 10);
  date.setUTCDate(date.getUTCDate() + 6);
  return { window_start_date: start, window_end_date: date.toISOString().slice(0, 10) };
}

type State = 'loading' | 'ready' | 'unavailable';
export interface MackCalendarStatus {
  kind: 'authentication' | 'running' | 'transport' | 'refused' | 'validation' | 'updated' | 'unchanged' | 'error';
  message: string;
  code?: string;
  eventCount?: number;
  reasonCodes?: string[];
  request_id?: string;
  job_id?: string;
  trace_id?: string;
  // A superseded request must not replace the current refresh's UI state.
  ignored?: boolean;
}

class CalendarError extends Error {
  constructor(readonly status: MackCalendarStatus) {
    super(status.message);
  }
}

const obj = (v: unknown): Record<string, unknown> | null =>
  v !== null && typeof v === 'object' && !Array.isArray(v) ? v as Record<string, unknown> : null;
const text = (v: unknown): v is string => typeof v === 'string';

function validUrl(v: unknown): boolean {
  if (!text(v) || !v) return true;
  try { const url = new URL(v); return ['https:', 'http:'].includes(url.protocol) && !url.username && !url.password; }
  catch { return false; }
}

const PULSE_STATES = new Set(['Scheduled', 'Running', 'Updated', 'Awaiting publication', 'Source unavailable', 'Release mismatch', 'Parse failed']);
function pulseDetails(row: Record<string, unknown>): MackPulseDetails {
  const legacyFailure = row.pulse_state === 'Failed';
  const pulse_state = text(row.pulse_state) && PULSE_STATES.has(row.pulse_state)
    ? row.pulse_state as MackPulseDetails['pulse_state'] : null;
  return { pulse_state,
    pulse_reason: text(row.pulse_reason) ? row.pulse_reason : legacyFailure ? 'Pulse outcome was not classified; daily reconciliation is required.' : null,
    pulse_attempted_at: text(row.pulse_attempted_at) ? row.pulse_attempted_at : null,
    pulse_source: text(row.pulse_source) && validUrl(row.pulse_source) ? row.pulse_source : null,
    pulse_next_reconciliation_at: text(row.pulse_next_reconciliation_at) ? row.pulse_next_reconciliation_at : null };
}
function downstreamDestinations(value: unknown): MackDownstreamDestination[] {
  if (!Array.isArray(value)) return [];
  return value.flatMap(entry => {
    const item = obj(entry);
    if (!item || !text(item.tab) || !item.tab) return [];
    return [{ tab: item.tab, sheet: text(item.sheet) && item.sheet ? item.sheet : 'UNBOUND',
      block: text(item.block) && item.block ? item.block : 'UNBOUND',
      workbook_named_input: text(item.workbook_named_input) ? item.workbook_named_input : '',
      consumer: text(item.consumer) ? item.consumer : '' }];
  });
}

export function parseMackCalendar(raw: unknown): MackCalendarView | null {
  const payload = obj(raw);
  const sourceRows = payload?.rows ?? payload?.scheduled;
  if (!payload || payload.success !== true || !Array.isArray(sourceRows)) return null;
  const scheduled: MackScheduledEvent[] = [];
  const seen = new Set<string>();
  const historical = payload.view === 'historical';
  const dependencySummary = obj(payload.dependency_summary);
  const coverageGaps = payload.coverage_gaps ?? dependencySummary?.contract_gaps;
  const officialExclusions = payload.official_observation_exclusions ?? payload.official_history_excluded;
  const workbook = obj(payload.history_workbook);
  const count = (value: unknown): value is number => typeof value === 'number' && Number.isSafeInteger(value) && value >= 0;
  const validWorkbook = !!workbook && text(workbook.name) && /^[A-Za-z0-9 _.-]+\.xlsx$/i.test(workbook.name)
    && !workbook.name.includes('..') && (workbook.state === 'SYNC_REQUIRED'
      || (workbook.state === 'AVAILABLE' && count(workbook.event_count) && count(workbook.vintage_count)));
  const completion = obj(payload.calendar_completion);
  const completionCode = completion?.reason_code;
  const validCompletion = !!completion && text(completion.state) && PULSE_STATES.has(completion.state)
    && !['Scheduled', 'Running', 'Updated'].includes(completion.state)
    && text(completionCode) && CALENDAR_ERROR_CODES.has(completionCode);
  for (const value of sourceRows) {
    const row = obj(value);
    if (!row || !text(row.event_id) || !row.event_id) continue;
    const name = row.event_name ?? row.name;
    const rowId = historical && text(row.history_id) && row.history_id ? row.history_id : row.event_id;
    if (!text(name) || !name || seen.has(rowId)) continue;
    const refs = Array.isArray(row.source_refs) ? row.source_refs.filter(ref => obj(ref) && validUrl(ref.url)) : [];
    const sourceUrl = row.source_url ?? refs.find(ref => text(ref.url) && !!ref.url)?.url;
    if (!validUrl(sourceUrl)) continue;
    seen.add(rowId);
    const valueText = (value: unknown, fallback = '') => text(value) ? value : typeof value === 'number' ? String(value) : fallback;
    const impact = String(row.impact || 'UNKNOWN').toUpperCase();
    const currency = valueText(row.currency, ({ US: 'USD', UK: 'GBP' } as Record<string, string>)[String(row.country)] || valueText(row.country));
    scheduled.push({ ...row,
      ...pulseDetails(row), downstream_destinations: downstreamDestinations(row.downstream_destinations),
      event_id: row.event_id, name,
      series_id: text(row.series_id) ? row.series_id : undefined,
      history_id: text(row.history_id) ? row.history_id : undefined,
      vintage: (row.vintage_at ?? row.vintage) == null ? null : valueText(row.vintage_at ?? row.vintage),
      revision_index: typeof row.revision_index === 'number' && Number.isSafeInteger(row.revision_index) && row.revision_index >= 0 ? row.revision_index : undefined,
      original_actual: row.original_actual == null ? null : valueText(row.original_actual),
      publication_at: text(row.publication_at) ? row.publication_at : null,
      retrieved_at_utc: text(row.retrieval_at ?? row.retrieved_at_utc) ? (row.retrieval_at ?? row.retrieved_at_utc) as string : null,
      day_label: valueText(row.event_date_local ?? row.day_label ?? row.event_date, '—'),
      time_local: valueText(row.event_time_local ?? row.time_local, '—'),
      timezone: valueText(row.display_timezone ?? row.timezone, 'Australia/Hobart'),
      starts_at: valueText(row.scheduled_at_utc ?? row.starts_at ?? row.scheduled_at),
      country: valueText(row.country, currency || '—'), currency,
      period: valueText(row.reference_period ?? row.period), subtitle: valueText(row.subtitle),
      consensus: valueText(row.consensus),
      previous: row.previous == null ? null : valueText(row.previous),
      forecast: (row.forecast ?? row.consensus) == null ? null : valueText(row.forecast ?? row.consensus),
      actual: row.actual == null ? null : valueText(row.actual),
      impact: ['HIGH', 'MEDIUM', 'LOW'].includes(impact) ? impact.toLowerCase() : 'UNKNOWN',
      wakes_sheet: valueText(row.affected_sheet ?? row.wakes_sheet, '—'),
      wakes_block: valueText(row.affected_block ?? row.wakes_block, '—'),
      source_refs: refs, source_url: sourceUrl, source_name: text(row.source_name) ? row.source_name : undefined, alert_set: row.alert_set === true,
    } as MackScheduledEvent);
  }
  if (sourceRows.length && !scheduled.length) return null;
  return { scheduled, unscheduled: Array.isArray(payload.unscheduled) ? payload.unscheduled : [], pulse: [],
    updated_at: text(payload.updated_at) ? payload.updated_at : null, status: String(payload.status || 'ok'),
    window_start_date: text(payload.window_start_date) ? payload.window_start_date : undefined,
    window_end_date: text(payload.window_end_date) ? payload.window_end_date : undefined,
    total_rows: typeof payload.total_rows === 'number' ? payload.total_rows : scheduled.length,
    filtered_rows: typeof payload.filtered_rows === 'number' ? payload.filtered_rows : scheduled.length,
    source: text(payload.source) ? payload.source : 'FXStreet',
    alerts: Array.isArray(payload.alerts) ? payload.alerts : scheduled.filter(row => row.alert_set),
    set_aside: Array.isArray(payload.set_aside) ? payload.set_aside : [],
    activation_forecast: Array.isArray(payload.activation_forecast) ? payload.activation_forecast.flatMap(value => {
      const item = obj(value);
      return item ? [{ ...item, ...pulseDetails(item), downstream_destinations: downstreamDestinations(item.downstream_destinations) } as MackCalendarView['activation_forecast'][number]] : [];
    }) : [],
    view: historical ? 'historical' : undefined,
    history_count: typeof payload.history_count === 'number' ? payload.history_count : undefined,
    history_series_count: typeof payload.history_series_count === 'number' ? payload.history_series_count : undefined,
    history_start_date: text(payload.history_start_date) ? payload.history_start_date : null,
    history_end_date: text(payload.history_end_date) ? payload.history_end_date : null,
    official_observation_count: typeof payload.official_observation_count === 'number' ? payload.official_observation_count
      : Array.isArray(payload.official_observations) ? payload.official_observations.length : undefined,
    official_observation_exclusions: Array.isArray(officialExclusions) ? officialExclusions.map(value => {
      const reason = obj(value)?.reason;
      const messages: Record<string, string> = {
        MALFORMED_OFFICIAL_OBSERVATION: 'Official observation schema is invalid.',
        OFFICIAL_PUBLICATION_TIME_UNPROVEN: 'Official publication time is unproven.',
        OFFICIAL_SOURCE_IDENTITY_UNPROVEN: 'Official source identity is unproven.',
        OFFICIAL_SERIES_OR_VINTAGE_IDENTITY_MISSING: 'Official series or vintage identity is missing.',
        OFFICIAL_ACTUAL_OR_UNIT_MISSING: 'Official Actual or unit is missing.',
        OFFICIAL_SOURCE_LINEAGE_MISSING: 'Official source lineage is missing.',
        OFFICIAL_RETRIEVAL_TIME_MISSING: 'Official retrieval time is missing.',
      };
      return text(reason) && messages[reason] ? messages[reason] : 'Official observation did not meet history requirements.';
    }) : [],
    coverage_gap_count: Array.isArray(coverageGaps) ? coverageGaps.length : undefined,
    history_retention: payload.history_retention === 'PERMANENT' ? 'PERMANENT' : undefined,
    history_workbook: validWorkbook ? { name: workbook!.name as string, event_count: count(workbook!.event_count) ? workbook!.event_count : null,
      vintage_count: count(workbook!.vintage_count) ? workbook!.vintage_count : null, updated_at: text(workbook!.updated_at) ? workbook!.updated_at : undefined,
      state: workbook!.state as 'AVAILABLE' | 'SYNC_REQUIRED' } : undefined,
    calendar_completion: validCompletion ? { state: completion!.state as string,
      reason: CALENDAR_PUBLIC_MESSAGES[completionCode as string],
      attempted_at: text(completion!.attempted_at) ? completion!.attempted_at : undefined,
      source_url: text(completion!.source) && validUrl(completion!.source) ? completion!.source : undefined,
      next_reconciliation_at: text(completion!.next_reconciliation_at) ? completion!.next_reconciliation_at : undefined } : undefined,
  };
}

export const CALENDAR_DIAGNOSTIC_LIMIT = 240;
const CALENDAR_REASON_LIMIT = 8;
export const CALENDAR_PUBLIC_MESSAGES: Readonly<Record<string, string>> = Object.freeze({
  CALENDAR_OPERATION_FAILED: 'Calendar operation could not complete.',
  CALENDAR_OUTCOME_UNVERIFIABLE: 'Calendar outcome could not be verified.',
  INCONSISTENT_TERMINAL_RESULT: 'Calendar outcome could not be verified.',
  CALENDAR_AUTHENTICATION_REQUIRED: 'Calendar authentication is required.',
  CALENDAR_OWNER_FORBIDDEN: 'This Calendar job is unavailable for this owner.',
  CALENDAR_SOURCE_POLICY_REFUSED: 'Calendar source policy refused this update.',
  CALENDAR_BACKEND_UNAVAILABLE: 'Calendar backend is unavailable. Check status before another refresh.',
  CALENDAR_CANCELLED: 'Calendar refresh was cancelled before admission.',
  CALENDAR_UNKNOWN_JOB: 'No verified Calendar outcome is available for this job.',
  CALENDAR_ADMISSION_ROLLED_BACK: 'Calendar admission rolled back; the previous generation is intact.',
  CALENDAR_REQUEST_REJECTED: 'Calendar request could not be validated.',
  CALENDAR_PENDING: 'Calendar admission is pending. Check status before another refresh.',
  CALENDAR_CANDIDATE_SET_ASIDE: 'Calendar candidate did not meet admission requirements.',
  CALENDAR_PUBLICATION_UNAVAILABLE: 'Calendar committed; live panel notification is unavailable.',
  CALENDAR_SOURCE_UNAVAILABLE: 'Source page unavailable',
  CALENDAR_AWAITING_PUBLICATION: 'The matching release has not published an Actual.',
  CALENDAR_RELEASE_MISMATCH: 'The returned release does not match the stored event and period.',
  CALENDAR_PARSE_FAILED: 'Fetched material could not be validated as the stored release.',
  CALENDAR_REQUIRED_RELEASES_UNAVAILABLE: 'Permitted FXStreet pages did not expose dated releases required by the dependency manifest.',
  CALENDAR_UNCITED_VALUE: 'Numeric value is uncited',
  CALENDAR_INVALID_CURRENCY: 'Invalid currency',
  CALENDAR_LEGACY_EXCLUDED: 'Unmatched legacy record remains in history',
  CALENDAR_MISSING_EVENT_IDENTITY: 'Missing event identity',
  CALENDAR_EVENT_UNMATCHED: 'No matching Calendar event was found.',
  CALENDAR_COMMITTED: 'Calendar admission committed and verified.',
  CALENDAR_UNCHANGED: 'Calendar research was not required; the stored generation is unchanged.',
  CALENDAR_STOP_REQUESTED: 'Calendar stop requested.',
  CALENDAR_ACQUIRING: 'Calendar acquisition running...',
  CALENDAR_SUPERSEDED: 'Calendar status belongs to a superseded refresh or authentication session; it was ignored.',
  CALENDAR_TIMEOUT: 'Calendar timeout: backend outcome is unknown. Check Calendar status before another refresh. No automatic retry was started.',
  CALENDAR_REQUEST_CANCELLED: 'Calendar request canceled; backend outcome is unknown. Check Calendar status before another refresh.',
  CALENDAR_TRANSPORT_UNKNOWN: 'Calendar transport failed: the request did not complete. Backend outcome is unknown; check Calendar status before another refresh.',
});
const CALENDAR_ERROR_CODES = new Set([
  'CALENDAR_OPERATION_FAILED', 'CALENDAR_OUTCOME_UNVERIFIABLE', 'INCONSISTENT_TERMINAL_RESULT', 'CALENDAR_AUTHENTICATION_REQUIRED',
  'CALENDAR_OWNER_FORBIDDEN', 'CALENDAR_SOURCE_POLICY_REFUSED', 'CALENDAR_BACKEND_UNAVAILABLE',
  'CALENDAR_CANCELLED', 'CALENDAR_UNKNOWN_JOB', 'CALENDAR_ADMISSION_ROLLED_BACK',
  'CALENDAR_REQUEST_REJECTED', 'CALENDAR_PENDING', 'CALENDAR_CANDIDATE_SET_ASIDE',
  'CALENDAR_SOURCE_UNAVAILABLE', 'CALENDAR_AWAITING_PUBLICATION', 'CALENDAR_RELEASE_MISMATCH', 'CALENDAR_PARSE_FAILED',
  'CALENDAR_REQUIRED_RELEASES_UNAVAILABLE',
]);
const CALENDAR_REASON_CODES = new Set([
  'CALENDAR_CANDIDATE_SET_ASIDE', 'CALENDAR_SOURCE_UNAVAILABLE', 'CALENDAR_UNCITED_VALUE',
  'CALENDAR_AWAITING_PUBLICATION', 'CALENDAR_RELEASE_MISMATCH', 'CALENDAR_PARSE_FAILED',
  'CALENDAR_INVALID_CURRENCY', 'CALENDAR_LEGACY_EXCLUDED', 'CALENDAR_MISSING_EVENT_IDENTITY', 'CALENDAR_EVENT_UNMATCHED',
]);
const reasonCode = (code: unknown): code is string => text(code) && CALENDAR_REASON_CODES.has(code);
function knownCode(value: unknown): value is string {
  return text(value) && Object.prototype.hasOwnProperty.call(CALENDAR_PUBLIC_MESSAGES, value);
}
export function calendarStatusMessage(status: MackCalendarStatus): string {
  if (status.code === 'CALENDAR_LOADED' && Number.isSafeInteger(status.eventCount) && status.eventCount! >= 0)
    return `${status.eventCount} sourced events loaded`;
  const base = knownCode(status.code) ? CALENDAR_PUBLIC_MESSAGES[status.code] : CALENDAR_PUBLIC_MESSAGES.CALENDAR_OPERATION_FAILED;
  const reasons = Array.isArray(status.reasonCodes) ? Array.from(new Set(status.reasonCodes.filter(reasonCode))).slice(0, CALENDAR_REASON_LIMIT).map(code => CALENDAR_PUBLIC_MESSAGES[code]) : [];
  return (base + (reasons.length ? ` ${reasons.join('; ')}` : '')).slice(0, CALENDAR_DIAGNOSTIC_LIMIT);
}
function publicStatus(kind: MackCalendarStatus['kind'], code: string, extra: Partial<MackCalendarStatus> = {}): MackCalendarStatus {
  const status = { kind, code, message: '', ...extra };
  status.message = calendarStatusMessage(status);
  return status;
}
// Compatibility helper has a closed set of literal public messages. It never
// sanitizes, truncates, or preserves any portion of untrusted diagnostic text.
export function calendarDiagnostic(message: string, limit = CALENDAR_DIAGNOSTIC_LIMIT): string {
  const safe = Object.values(CALENDAR_PUBLIC_MESSAGES).includes(message) ? message : CALENDAR_PUBLIC_MESSAGES.CALENDAR_OPERATION_FAILED;
  return safe.slice(0, Math.min(CALENDAR_DIAGNOSTIC_LIMIT, Math.max(0, limit)));
}
export function calendarSetAsideReasons(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  const reasons: string[] = [];
  for (const entry of value.slice(0, 32)) {
    const record = obj(entry);
    if (!record) continue;
    const codes = Array.isArray(record.reason_codes) ? record.reason_codes.slice(0, 16) : [record.error_code];
    for (const code of codes.length ? codes : [undefined]) {
      const message = reasonCode(code) ? CALENDAR_PUBLIC_MESSAGES[code] : CALENDAR_PUBLIC_MESSAGES.CALENDAR_CANDIDATE_SET_ASIDE;
      if (!reasons.includes(message)) reasons.push(message);
      if (reasons.length >= CALENDAR_REASON_LIMIT) return reasons;
    }
  }
  return reasons;
}
function responseFailure(data: unknown, httpStatus: number): MackCalendarStatus {
  const payload = obj(data);
  let code = text(payload?.error_code) && CALENDAR_ERROR_CODES.has(payload!.error_code as string) ? payload!.error_code as string : 'CALENDAR_OPERATION_FAILED';
  if (httpStatus === 401) code = 'CALENDAR_AUTHENTICATION_REQUIRED';
  else if (httpStatus === 403) code = 'CALENDAR_OWNER_FORBIDDEN';
  else if (!payload?.error_code && ['QUEUED', 'RUNNING', 'running', 'PREPARED', 'COMMITTING', 'stopping'].includes(String(payload?.state))) code = 'CALENDAR_PENDING';
  const kinds: Record<string, MackCalendarStatus['kind']> = {
    CALENDAR_AUTHENTICATION_REQUIRED: 'authentication', CALENDAR_OWNER_FORBIDDEN: 'authentication',
    CALENDAR_SOURCE_POLICY_REFUSED: 'refused', CALENDAR_REQUEST_REJECTED: 'validation',
    CALENDAR_PENDING: 'running', CALENDAR_BACKEND_UNAVAILABLE: 'transport',
    CALENDAR_OUTCOME_UNVERIFIABLE: 'validation', INCONSISTENT_TERMINAL_RESULT: 'validation', CALENDAR_UNKNOWN_JOB: 'unchanged',
  };
  const reasonCodes = Array.isArray(payload?.set_aside) ? payload!.set_aside.slice(0, 32).flatMap((entry: unknown) => {
    const record = obj(entry);
    return Array.isArray(record?.reason_codes) ? record!.reason_codes.slice(0, 16).filter(reasonCode) : [];
  }) : [];
  return publicStatus(kinds[code] || 'error', code, { reasonCodes });
}
export function calendarFailure(error: unknown): MackCalendarStatus {
  if (error instanceof CalendarError) return error.status;
  if (axios.isAxiosError(error) && error.response) return responseFailure(error.response.data, error.response.status);
  return publicStatus('transport', 'CALENDAR_TRANSPORT_UNKNOWN');
}

export const CALENDAR_READ_TIMEOUT_MS = 20_000;
export const CALENDAR_REFRESH_TIMEOUT_MS = 170_000;
interface CalendarIdentity { request_id: string; job_id: string; trace_id: string }
interface CalendarSession { token: string; version: number; identity?: CalendarIdentity; terminal?: MackCalendarStatus }
let calendarSession: CalendarSession | undefined;
function session(): CalendarSession {
  const token = authToken();
  if (!calendarSession || calendarSession.token !== token) calendarSession = { token, version: 0 };
  return calendarSession;
}
function validIdentity(value: unknown): value is string {
  return text(value) && /^[A-Za-z0-9_-]{1,160}$/.test(value);
}
function currentRequest(owner: CalendarSession, version: number): boolean {
  return session() === owner && owner.version === version;
}
const superseded = (): MackCalendarStatus => publicStatus('running', 'CALENDAR_SUPERSEDED', { ignored: true });
const identityRefusal = (): MackCalendarStatus => publicStatus('validation', 'CALENDAR_OUTCOME_UNVERIFIABLE');
function identityMatches(payload: Record<string, unknown> | null, expected: CalendarIdentity): boolean {
  return !!payload && (['request_id', 'job_id', 'trace_id'] as const).every(key =>
    validIdentity(payload[key]) && payload[key] === expected[key]);
}
function bindRefreshResponse(payload: Record<string, unknown> | null, owner: CalendarSession, version: number): boolean {
  return currentRequest(owner, version) && !!owner.identity && identityMatches(payload, owner.identity);
}
type CalendarResult = 'committed' | 'unchanged' | 'pending' | 'failed' | 'cancelled' | 'unknown' | 'inconsistent';
const COMMITTED_STATES = new Set(['complete', 'COMMITTED']);
const UNCHANGED_STATES = new Set(['NO_RESEARCH_REQUIRED']);
const PENDING_STATES = new Set(['QUEUED', 'RUNNING', 'running', 'PREPARED', 'COMMITTING', 'stopping']);
const FAILED_STATES = new Set(['FAILED', 'failed', 'refused', 'blocked', 'ROLLED_BACK', 'rejected', 'NO_LOOK', 'no_valid_events', 'FORBIDDEN', 'off']);
const CANCELLED_STATES = new Set(['CANCELLED', 'cancelled', 'stopped']);
const UNKNOWN_STATES = new Set(['UNKNOWN', 'UNVERIFIABLE', 'RECOVERY_REQUIRED']);
// Both public response paths cross this boundary before applying a view or
// caching a terminal claim. Identity proves whose result this is; the explicit
// terminal fields must independently agree before they prove success.
function classifyCalendarResult(payload: Record<string, unknown>): CalendarResult {
  const state = payload.state;
  if (typeof payload.success !== 'boolean' || !text(state)) return 'inconsistent';
  for (const key of ['running', 'outcome_pending', 'verified', 'terminal', 'retryable'] as const)
    if (payload[key] !== undefined && typeof payload[key] !== 'boolean') return 'inconsistent';
  const committed = COMMITTED_STATES.has(state);
  const unchanged = UNCHANGED_STATES.has(state);
  if (payload.success) {
    if ((!committed && !unchanged) || payload.terminal_state !== 'committed' || payload.running !== false
      || (payload.outcome_pending !== undefined && payload.outcome_pending !== false)
      || (payload.verified !== undefined && payload.verified !== true)
      || (payload.terminal !== undefined && payload.terminal !== true)
      || (payload.retryable !== undefined && payload.retryable !== false)
      || (payload.classification !== undefined && payload.classification !== 'terminal')) return 'inconsistent';
    if (payload.error_code !== undefined
      && !(unchanged ? ['CALENDAR_UNCHANGED'] : ['CALENDAR_COMMITTED', 'CALENDAR_PUBLICATION_UNAVAILABLE']).includes(String(payload.error_code))) return 'inconsistent';
    // A projected storage status is business data; a terminal status word must
    // agree with the authoritative state rather than override it.
    if (payload.status !== undefined && CALENDAR_JOB_OUTCOME_STATES.has(String(payload.status))
      && !(unchanged ? UNCHANGED_STATES : COMMITTED_STATES).has(String(payload.status)) && payload.status !== 'committed') return 'inconsistent';
    if (payload.acquisition !== undefined) {
      const acquisition = obj(payload.acquisition);
      const expected = unchanged ? 'PUBLIC_WEB_RESEARCH_CLI_AVAILABLE_UNCHANGED_NO_WRITE'
        : 'PUBLIC_WEB_RESEARCH_CLI_ADMITTED_TO_PRODUCTION_CALENDAR_PATH';
      if (!acquisition || (acquisition.state !== undefined && acquisition.state !== state && acquisition.state !== expected)) return 'inconsistent';
    }
    const receipt = obj(payload.receipt);
    const priorSuccess = obj(payload.last_success);
    // A genuine duplicate admission reads the same paired generation without
    // writing. Its stored last-success belongs to that earlier generation/job.
    const preservedGeneration = unchanged || (!!receipt && !!priorSuccess
      && receipt.written === 0 && receipt.projection_updated === false
      && receipt.t2_readback === true && receipt.operational_readback === true
      && text(receipt.generation) && /^calgen_[a-f0-9]{32}$/.test(receipt.generation)
      && priorSuccess.generation === receipt.generation
      && (payload.admission_generation === undefined || payload.admission_generation === receipt.generation));
    const generations: unknown[] = [];
    if (payload.admission_generation != null) generations.push(payload.admission_generation);
    for (const key of ['receipt', 'last_success'] as const) {
      if (payload[key] === undefined) continue;
      if (key === 'last_success' && payload[key] === null) continue;
      const record = obj(payload[key]);
      if (!record) return 'inconsistent';
      for (const identity of ['request_id', 'job_id', 'trace_id'] as const)
        if (!(preservedGeneration && key === 'last_success') && record[identity] !== undefined && record[identity] !== payload[identity]) return 'inconsistent';
      if (record.generation != null) generations.push(record.generation);
      if (key === 'receipt') {
        if (record.written !== undefined && (!Number.isSafeInteger(record.written) || (record.written as number) < 0 || (unchanged && record.written !== 0))) return 'inconsistent';
        for (const readback of ['t2_readback', 'operational_readback'] as const)
          if (record[readback] !== undefined && (typeof record[readback] !== 'boolean' || (committed && record[readback] !== true))) return 'inconsistent';
      }
    }
    if (generations.some(generation => !text(generation) || !/^calgen_[a-f0-9]{32}$/.test(generation) || generation !== generations[0])) return 'inconsistent';
    return unchanged ? 'unchanged' : 'committed';
  }
  if (committed || unchanged || payload.verified === true || payload.terminal_state === 'committed') return 'inconsistent';
  if (PENDING_STATES.has(state)) {
    if (payload.terminal_state !== undefined || payload.terminal === true) return 'inconsistent';
    return 'pending';
  }
  const terminal = payload.terminal_state;
  if (terminal !== undefined && !['failed', 'cancelled', 'rolled_back'].includes(String(terminal))) return 'inconsistent';
  if (payload.running === true || payload.outcome_pending === true) {
    if (FAILED_STATES.has(state) || CANCELLED_STATES.has(state) || terminal !== undefined) return 'inconsistent';
  }
  if (FAILED_STATES.has(state)) return terminal === 'cancelled' ? 'inconsistent' : 'failed';
  if (CANCELLED_STATES.has(state)) return terminal !== undefined && terminal !== 'cancelled' ? 'inconsistent' : 'cancelled';
  if (state === 'TIMED_OUT') return terminal === undefined ? 'unknown' : terminal === 'failed' ? 'failed' : 'inconsistent';
  if (UNKNOWN_STATES.has(state)) return terminal === undefined && payload.terminal !== true ? 'unknown' : 'inconsistent';
  return 'inconsistent';
}
const inconsistentResult = (identity: CalendarIdentity): MackCalendarStatus =>
  publicStatus('validation', 'INCONSISTENT_TERMINAL_RESULT', identity);
const CALENDAR_JOB_OUTCOME_STATES = new Set([
  'QUEUED', 'RUNNING', 'running', 'PREPARED', 'COMMITTING', 'stopping', 'RECOVERY_REQUIRED',
  'complete', 'COMMITTED', 'committed', 'FAILED', 'failed', 'CANCELLED', 'cancelled',
  'stopped', 'ROLLED_BACK', 'TIMED_OUT', 'UNKNOWN', 'UNVERIFIABLE', 'refused',
]);
const CALENDAR_JOB_OUTCOME_CODES = new Set([
  'CALENDAR_PENDING', 'CALENDAR_CANCELLED', 'CALENDAR_ADMISSION_ROLLED_BACK', 'CALENDAR_UNKNOWN_JOB',
  'CALENDAR_OUTCOME_UNVERIFIABLE', 'CALENDAR_COMMITTED', 'CALENDAR_UNCHANGED', 'CALENDAR_PUBLICATION_UNAVAILABLE',
]);
function refreshClaimsIdentity(payload: Record<string, unknown> | null, httpStatus: number): boolean {
  if (httpStatus === 401 || httpStatus === 403) return false;
  if (!payload) return false;
  return payload.success === true || payload.verified !== undefined || payload.terminal_state !== undefined
    || ['request_id', 'job_id', 'trace_id'].some(key => Object.prototype.hasOwnProperty.call(payload, key))
    || CALENDAR_JOB_OUTCOME_STATES.has(String(payload.state))
    || CALENDAR_JOB_OUTCOME_STATES.has(String(payload.status))
    || obj(payload.acquisition)?.state !== undefined
    || CALENDAR_JOB_OUTCOME_CODES.has(String(payload.error_code));
}
function refreshFailure(data: unknown, httpStatus: number, owner: CalendarSession, version: number): MackCalendarStatus {
  const payload = obj(data);
  const bound = bindRefreshResponse(payload, owner, version);
  if (refreshClaimsIdentity(payload, httpStatus) && !bound) return identityRefusal();
  const classification = bound && httpStatus !== 401 && httpStatus !== 403
    ? classifyCalendarResult(payload!) : undefined;
  if (classification === 'inconsistent') return inconsistentResult(owner.identity!);
  const status = responseFailure(payload, httpStatus);
  if (bound && (classification === 'failed' || classification === 'cancelled')) owner.terminal = status;
  return status;
}
async function boundedCalendarRequest<T>(
  action: (signal: AbortSignal) => Promise<T>, milliseconds: number, external?: AbortSignal,
): Promise<T> {
  const controller = new AbortController();
  let timer: ReturnType<typeof setTimeout> | undefined;
  let abort: () => void = () => {};
  const terminal = new Promise<never>((_, reject) => {
    const stop = (code: string) => {
      reject(new CalendarError(publicStatus('transport', code)));
      controller.abort();
    };
    abort = () => stop('CALENDAR_REQUEST_CANCELLED');
    external?.addEventListener('abort', abort, { once: true });
    timer = setTimeout(() => stop('CALENDAR_TIMEOUT'), milliseconds);
  });
  try {
    if (external?.aborted) abort();
    return await Promise.race([terminal, action(controller.signal)]);
  } finally {
    if (timer !== undefined) clearTimeout(timer);
    external?.removeEventListener('abort', abort);
  }
}

export async function checkMackCalendarStatus(): Promise<MackCalendarStatus> {
  const owner = session();
  const version = owner.version;
  const expected = owner.identity && { ...owner.identity };
  if (!expected) return publicStatus('unchanged', 'CALENDAR_UNKNOWN_JOB');
  const settle = (data: unknown, httpStatus: number): MackCalendarStatus => {
    if (!currentRequest(owner, version)) return superseded();
    const payload = obj(data);
    if (httpStatus === 401 || httpStatus === 403) return responseFailure(payload, httpStatus);
    if (!identityMatches(payload, expected)) return identityRefusal();
    const classification = classifyCalendarResult(payload!);
    if (classification === 'inconsistent') return inconsistentResult(expected);
    if (owner.terminal) return owner.terminal;
    if (classification === 'unchanged') {
      owner.terminal = publicStatus('unchanged', 'CALENDAR_UNCHANGED');
      return owner.terminal;
    }
    if (classification === 'committed') {
      owner.terminal = publicStatus('updated', 'CALENDAR_COMMITTED');
      return owner.terminal;
    }
    const result = responseFailure(payload, httpStatus);
    if (classification === 'failed' || classification === 'cancelled') owner.terminal = result;
    return result;
  };
  try {
    const response = await boundedCalendarRequest(signal => axios.get(`${apiBase()}/mack/calendar/status`,
      { signal, params: expected }), CALENDAR_READ_TIMEOUT_MS);
    return settle(response.data, response.status);
  } catch (failure) {
    if (!currentRequest(owner, version)) return superseded();
    if (axios.isAxiosError(failure) && failure.response) return settle(failure.response.data, failure.response.status);
    return calendarFailure(failure);
  }
}

export async function fetchMackCalendar(signal: AbortSignal, dateWindow?: CalendarWindow): Promise<MackCalendarView> {
  // The shared Axios interceptor in App.tsx attaches the current owner Bearer.
  const response = await boundedCalendarRequest(requestSignal => axios.get(`${apiBase()}/mack/calendar`, { signal: requestSignal, params: dateWindow }), CALENDAR_READ_TIMEOUT_MS, signal);
  if (obj(response.data)?.success === false) {
    throw new CalendarError(responseFailure(response.data, response.status));
  }
  const view = parseMackCalendar(response.data);
  if (!view) throw new CalendarError(publicStatus('validation', 'CALENDAR_REQUEST_REJECTED'));
  return view;
}

export async function refreshMackCalendar(dateWindow?: CalendarWindow): Promise<MackCalendarStatus & { view: MackCalendarView }> {
  // Mint correlation before transport: even a lost POST response can be read
  // back by exact job identity. These identifiers carry no source truth.
  const owner = session();
  const version = ++owner.version;
  owner.identity = undefined;
  owner.terminal = undefined;
  if (!window.crypto?.getRandomValues) throw new CalendarError(publicStatus('validation', 'CALENDAR_REQUEST_REJECTED'));
  const nonce = Array.from(window.crypto.getRandomValues(new Uint8Array(16)),
    value => value.toString(16).padStart(2, '0')).join('');
  const identity = { request_id: `req_mack_calendar_${nonce}`, job_id: `job_mack_calendar_${nonce}`,
    trace_id: `trace_mack_calendar_${nonce}` };
  owner.identity = identity;
  let response;
  try {
    response = await boundedCalendarRequest(signal => axios.post(`${apiBase()}/mack/calendar/refresh`,
      { ...dateWindow, ...identity }, { timeout: 0, signal }), CALENDAR_REFRESH_TIMEOUT_MS);
  } catch (failure) {
    if (!currentRequest(owner, version)) throw new CalendarError(superseded());
    if (axios.isAxiosError(failure) && failure.response)
      throw new CalendarError(refreshFailure(failure.response.data, failure.response.status, owner, version));
    throw failure;
  }
  if (!currentRequest(owner, version)) throw new CalendarError(superseded());
  const payload = obj(response.data);
  if (payload?.success === true && !bindRefreshResponse(payload, owner, version)) throw new CalendarError(identityRefusal());
  if (payload?.success === true) {
    const classification = classifyCalendarResult(payload);
    if (classification !== 'committed' && classification !== 'unchanged') throw new CalendarError(inconsistentResult(identity));
    const view = parseMackCalendar(payload);
    if (!view) throw new CalendarError(publicStatus('validation', 'CALENDAR_REQUEST_REJECTED'));
    const kind = classification === 'unchanged' ? 'unchanged' : 'updated';
    if (classification === 'committed') owner.terminal = publicStatus('updated', 'CALENDAR_COMMITTED');
    else if (kind === 'unchanged') owner.terminal = publicStatus('unchanged', 'CALENDAR_UNCHANGED');
    return { ...publicStatus(kind, 'CALENDAR_LOADED', { eventCount: view.scheduled.length }), view };
  }
  throw new CalendarError(refreshFailure(response.data, response.status, owner, version));
}

export async function stopMackCalendar(): Promise<MackCalendarStatus> {
  const response = await axios.post(`${apiBase()}/mack/calendar/stop`, {});
  if (obj(response.data)?.success !== true) throw new CalendarError(responseFailure(response.data, response.status));
  return publicStatus('running', 'CALENDAR_STOP_REQUESTED');
}

export async function setMackCalendarAlert(event_id: string, armed: boolean, dateWindow: CalendarWindow): Promise<MackCalendarView> {
  const response = await axios.post(`${apiBase()}/mack/calendar/alert`,
    { event_id, armed, fires: ['morning-of', 'T-2h'], ...dateWindow });
  const view = parseMackCalendar(response.data);
  if (!view) throw new CalendarError(responseFailure(response.data, response.status));
  return view;
}

export function useMackCalendar(suppliedToken?: string | null, dateWindow?: CalendarWindow) {
  const [view, setView] = useState<MackCalendarView | null>(null);
  const [state, setState] = useState<State>('loading');
  const [error, setError] = useState<MackCalendarStatus | null>(null);
  const current = useRef<AbortController | null>(null);
  const token = suppliedToken || authToken();

  const apply = useCallback((next: MackCalendarView) => {
    // An older in-flight GET must not overwrite the POST's committed rows.
    current.current?.abort();
    setView(next); setState('ready'); setError(null);
  }, []);

  const reload = useCallback(async () => {
    current.current?.abort();
    const controller = new AbortController(); current.current = controller;
    setState('loading');
    setError(null);
    try {
      const next = await fetchMackCalendar(controller.signal, dateWindow);
      if (!controller.signal.aborted) { setView(next); setState('ready'); }
    } catch (failure) {
      if (!controller.signal.aborted) { setError(calendarFailure(failure)); setState('unavailable'); }
    }
  // Window keys are stable across normal renders. Navigation reads storage only.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateWindow?.window_start_date, dateWindow?.window_end_date, dateWindow?.view]);

  useEffect(() => {
    void reload();
    const socket = getRexySocket();
    const onPanel = (detail: any) => {
      if (detail?.domain === 'TRADING' && detail?.agent === 'MACK'
        && detail?.semantic_address === 'calendar.calendar' && detail?.panel === 'Calendar') void reload();
    };
    socket.on('mack:calendar:schedule-updated', onPanel);
    const onWindow = (event: Event) => onPanel((event as CustomEvent).detail);
    window.addEventListener('mack-calendar-updated', onWindow);
    return () => {
      current.current?.abort();
      socket.off('mack:calendar:schedule-updated', onPanel);
      window.removeEventListener('mack-calendar-updated', onWindow);
    };
  }, [token, reload]);

  return { view, state, error, reload, apply };
}
